/**
 * Google Address Validation service wrapper
 * Calls our Next.js API route at /api/address/validate and adapts the
 * response into the legacy shape used by the checkout context and modal.
 */

/**
 * Map Google issues to legacy EasyPost-like error codes used by UI
 * Only minimal mappings required by current UI logic are provided.
 *
 * - Missing ZIP => E.ZIP.NOT_FOUND
 * - Missing street number => E.HOUSE_NUMBER.MISSING
 * - Undeliverable/invalid address => E.ADDRESS.NOT_FOUND
 * - Invalid ZIP when tokens include a zip-like token (best-effort)
 * @param {Array<Object>} issues - Array of Google address validation issues
 * @returns {Array<{code: string}>} Array of error objects with legacy error codes
 */
function mapIssuesToLegacyErrors(issues = []) {
  const errors = []

  for (const issue of issues) {
    const code = issue?.code
    const component = (issue?.component || '').toLowerCase()
    const token = issue?.token

    if (code === 'MISSING_COMPONENT') {
      if (component.includes('zip')) {
        errors.push({ code: 'E.ZIP.NOT_FOUND' })
        continue
      }
      if (component.includes('street number')) {
        errors.push({ code: 'E.HOUSE_NUMBER.MISSING' })
        continue
      }
      // Unhandled missing component -> generic not found
      errors.push({ code: 'E.ADDRESS.NOT_FOUND' })
      continue
    }

    if (code === 'UNCONFIRMED_COMPONENT') {
      // If Google marks ZIP as unconfirmed, surface as invalid ZIP
      const lowered = (issue?.component || '').toLowerCase()
      if (lowered.includes('zip')) {
        errors.push({ code: 'E.ZIP.INVALID' })
      } else if (lowered.includes('street number')) {
        errors.push({ code: 'E.HOUSE_NUMBER.MISSING' })
      } else {
        errors.push({ code: 'E.ADDRESS.NOT_FOUND' })
      }
      continue
    }

    if (code === 'UNRESOLVED_TOKEN') {
      // Best effort: try to detect invalid zip tokens
      if (typeof token === 'string' && /\b\d{5}(-\d{4})?\b/.test(token)) {
        errors.push({ code: 'E.ZIP.INVALID' })
      } else {
        errors.push({ code: 'E.ADDRESS.NOT_FOUND' })
      }
      continue
    }

    if (code === 'USPS_DPV_MISSING_UNIT') {
      // Missing apartment/suite/unit: surface as generic address issue
      errors.push({ code: 'E.ADDRESS.NOT_FOUND' })
      continue
    }

    if (code === 'USPS_DPV_INVALID_UNIT' || code === 'USPS_DPV_UNDELIVERABLE') {
      errors.push({ code: 'E.ADDRESS.NOT_FOUND' })
      continue
    }

    // Fallback
    errors.push({ code: 'E.ADDRESS.NOT_FOUND' })
  }

  // De-duplicate by code
  const seen = new Set()
  return errors.filter((e) => {
    if (seen.has(e.code)) return false
    seen.add(e.code)
    return true
  })
}

/**
 * Convert Google postalAddress to our normalized address shape
 * @param {Object|null|undefined} suggestedPostalAddress - Google postal address object
 * @returns {Object|null} Normalized address object with address1, address2, city, province, zip, country
 */
function normalizeSuggestedAddress(suggestedPostalAddress) {
  if (!suggestedPostalAddress) return null
  const lines = Array.isArray(suggestedPostalAddress.addressLines)
    ? suggestedPostalAddress.addressLines
    : []
  return {
    address1: lines[0] || '',
    address2: lines[1] || '',
    city: suggestedPostalAddress.locality || '',
    province: suggestedPostalAddress.administrativeArea || '',
    zip: suggestedPostalAddress.postalCode || '',
    country: suggestedPostalAddress.regionCode || 'US',
  }
}

/**
 * Validates an address using our Google endpoint
 * @param {Object} address
 * @param {string} address.street1
 * @param {string} [address.street2]
 * @param {string} address.city
 * @param {string} address.state
 * @param {string} address.zip
 * @param {string} address.country
 * @param {Object} [options]
 * @param {string} [options.previousResponseId]
 * @param {string} [options.sessionToken]
 * @returns {Promise<{isValid:boolean, normalizedAddress:Object|null, errors:Array, raw?:Object}>}
 */
export async function validateAddress(address, options = {}) {
  try {
    const { street1, street2, city, state, zip, country } = address || {}

    // Build payload matching /api/address/validate expectations
    const payload = {
      address: {
        // For Google AV API, prefer structured fields
        addressLines: [street1, street2].filter(Boolean),
        locality: city,
        administrativeArea: state,
        postalCode: zip,
        // regionCode is enforced to US in the API route; send along anyway
        regionCode: country || 'US',
      },
      previousResponseId: options.previousResponseId,
      sessionToken: options.sessionToken,
    }

    const response = await fetch('/api/address/validate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })

    if (!response.ok) {
      const errorPayload = await response.json().catch(() => ({}))
      throw new Error(errorPayload?.error || response.statusText)
    }

    const data = await response.json()

    const mappedErrors = mapIssuesToLegacyErrors(data?.issues || [])

    // Client-side US ZIP sanity check to ensure short/invalid ZIPs prompt the modal
    const errors = [...mappedErrors]
    const usZipRegex = /^\d{5}(-\d{4})?$/
    const providedZip = (zip || '').trim()
    if ((country || 'US') === 'US' && providedZip && !usZipRegex.test(providedZip)) {
      if (!errors.some((e) => e.code === 'E.ZIP.INVALID')) {
        errors.push({ code: 'E.ZIP.INVALID' })
      }
    }

    // If there are relevant errors, force invalid so UI can prompt for fields
    const relevantErrorCodes = new Set([
      'E.HOUSE_NUMBER.MISSING',
      'E.ZIP.NOT_FOUND',
      'E.ZIP.INVALID',
      'E.ADDRESS.NOT_FOUND',
    ])
    const hasRelevantErrors = errors.some((e) => relevantErrorCodes.has(e.code))
    const isValid = !!data?.ok && !hasRelevantErrors

    const legacy = {
      isValid,
      normalizedAddress: normalizeSuggestedAddress(data?.suggestedPostalAddress),
      errors,
      raw: data,
    }

    return legacy
  } catch (error) {
    // Error is already handled by the API route, just return invalid state
    return { isValid: false, errors: [{ code: 'E.ADDRESS.NOT_FOUND' }], error: error?.message }
  }
}


